import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { LoginForm } from './LoginForm';
import { SignUpForm } from './SignUpForm';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { EmailVerificationForm } from './EmailVerificationForm';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'signup';
}

export function AuthModal({ isOpen, onClose, initialMode = 'login' }: AuthModalProps) {
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isEmailVerification, setIsEmailVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationType, setVerificationType] = useState<'signup' | 'recovery'>('signup');
  const navigate = useNavigate();
  
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isLoading: profileLoading } = useProfile();
  
  const isLoading = authLoading || profileLoading;

  useEffect(() => {
    setIsLogin(initialMode === 'login');
    setIsForgotPassword(false);
    setIsEmailVerification(false);
  }, [isOpen, initialMode]);

  useEffect(() => {
    if (isOpen && isAuthenticated && !isLoading) {
      onClose();
      navigate('/home');
    }
  }, [isAuthenticated, isLoading, isOpen, navigate, onClose]);

  const handleAuthSuccess = () => {
    toast.success('Login Berhasil!', { description: 'Selamat datang kembali!' });
    onClose();
    navigate('/home');
  };

  const handleLogin = () => {
    handleAuthSuccess();
  };


  const handleSwitchToForgotPassword = () => setIsForgotPassword(true);
  const handleSwitchFromForgotPassword = () => setIsForgotPassword(false);

  const handleForgotPassword = async (email: string) => {
    try {
      const response = await api.forgotPassword(email);
      return response;
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const handleSwitchToEmailVerification = (email: string, type: 'signup' | 'recovery') => {
    setVerificationEmail(email);
    setVerificationType(type);
    setIsEmailVerification(true);
  };

  const handleSwitchFromEmailVerification = () => {
    setIsEmailVerification(false);
    setVerificationEmail('');
  };

  const handleEmailVerified = () => {
    setIsEmailVerification(false);
    setVerificationEmail('');
    handleAuthSuccess();
  };

  const handleResendSuccess = () => {
    toast.success('Verification email sent successfully!');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px] p-0 overflow-hidden bg-background/60 backdrop-blur-2xl border-white/10 shadow-2xl">
        <div className="p-6">
          {isEmailVerification ? (
            <EmailVerificationForm
              email={verificationEmail}
              type={verificationType}
              onBack={handleSwitchFromEmailVerification}
              onVerified={handleEmailVerified}
              onResendSuccess={handleResendSuccess}
            />
          ) : isForgotPassword ? (
            <ForgotPasswordForm
              onSwitchToLogin={handleSwitchFromForgotPassword}
              onForgotPassword={handleForgotPassword}
            />
          ) : isLogin ? (
            <LoginForm
              onSwitchToSignUp={() => setIsLogin(false)}
              onSwitchToForgotPassword={handleSwitchToForgotPassword}
              onLogin={handleLogin}
            />
          ) : (
            <SignUpForm
              onSwitchToLogin={() => setIsLogin(true)}
              onSwitchToEmailVerification={handleSwitchToEmailVerification}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
